// Case Data: the two-party input grid with expander rows, alternating shading, drop-down choice values, automatic
// values (asterisk), expand/collapse all and show/hide zero rows. Money is entered in the case period (Monthly/Yearly).
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import type { Case, FilingStatus, PartyIndex } from '@divorceos/schema';
import { act } from '../actions';
import { useCaseView } from '../caseView';
import { helpFor } from '../help';
import { factor, periodWord } from '../model';
import { FILINGS, ROWS, flattenRows, moneyValue, type FlatLine, type GridRow, type SubRow } from '../rows';
import { useDis } from '../store';
import { childrenWith } from '../../lib/rows';
import { timeshareWith } from '../../lib/children';
import { INPUT_PERIODS, INPUT_PERIOD_LABEL, PERIOD_PER_MONTH, type CellMeta, type InputPeriod } from '../appData';

const NUMERIC = /^-?\d*\.?\d+$/;
const fmtInt = (n: number): string => Math.round(n).toLocaleString('en-US');
const round2 = (n: number): number => Math.round(n * 100) / 100;
const PERIOD_BADGE: Record<InputPeriod, string> = { monthly: '', semimonthly: 'SM', biweekly: '2W', weekly: '1W', annual: 'YR' };

/** Live thousands grouping for an integer string, keeping the caret after the same digit. */
function groupInt(raw: string): string {
  const neg = /^\s*-/.test(raw);
  const digits = raw.replace(/[^\d]/g, '');
  return (neg && digits ? '-' : '') + (digits ? Number(digits).toLocaleString('en-US') : neg ? '-' : '');
}

/** Text input for a number: shows the formatted value, edits live, reformats on blur.
 *  ENG-02: a blank entry means zero; non-numeric text is flagged and not silently committed as 0.
 *  C18 groups thousands live; C17 adds a per-field pay-period (money cells, monthly mode); C23 a footnote. */
interface NumCellProps {
  value: number;
  onChange: (n: number) => void;
  label: string;
  decimals?: boolean;
  cellId: string;
  /** Per-cell metadata (period/note); undefined = none. */
  meta?: CellMeta;
  setMeta?: (m: CellMeta | null) => void;
  /** Offer the per-field pay-period selector (money cells in monthly mode only). */
  allowPeriod?: boolean;
}

function NumCell({ value, onChange, label, decimals = false, cellId, meta, setMeta, allowPeriod = false }: NumCellProps) {
  const [text, setText] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const perRef = useRef<HTMLSelectElement>(null);
  const period: InputPeriod = allowPeriod ? meta?.period ?? 'monthly' : 'monthly';
  const pf = PERIOD_PER_MONTH[period];           // value (monthly) = entered-in-period × pf
  const inPeriod = value / pf;                   // what the user types for this period
  const fmt = (n: number) => (decimals ? String(round2(n)) : fmtInt(n));
  const shown = text ?? fmt(value);              // display stays monthly at rest
  const cleaned = text === null ? '' : text.replace(/[$,\s]/g, '');
  const invalid = text !== null && cleaned !== '' && !NUMERIC.test(cleaned);
  const hasNote = !!meta?.note;

  const commit = (cl: string) => {
    if (cl === '') { onChange(0); return; }
    if (NUMERIC.test(cl)) { const n = parseFloat(cl); if (Number.isFinite(n)) onChange(n * pf); }
  };
  const setPeriod = (p: InputPeriod) => {
    if (!setMeta) return;
    const next = { ...meta }; if (p === 'monthly') delete next.period; else next.period = p;
    setMeta(next.period || next.note || next.bucket || next.estimate ? next : null);
    setText(null);                               // re-derive the in-period amount from the stored value
  };
  const setNote = (note: string, onReport: boolean) => {
    if (!setMeta) return;
    const next = { ...meta };
    // Store the raw text (not trimmed) so spaces can be typed between words; trim only to test for empty.
    if (note.trim()) { next.note = note; if (onReport) next.noteOnReport = true; else delete next.noteOnReport; } else { delete next.note; delete next.noteOnReport; }
    setMeta(next.period || next.note || next.bucket || next.estimate ? next : null);
  };
  const toggleEstimate = () => {
    if (!setMeta) return;
    const next = { ...meta };
    if (next.estimate) delete next.estimate; else next.estimate = true;
    setMeta(next.period || next.note || next.bucket || next.estimate ? next : null);
  };

  return (
    <span className="numcell" style={{ position: 'relative' }}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) { setFocused(false); setNoteOpen(false); setText(null); } }}>
      <input data-cell={cellId} inputMode="decimal" aria-label={label} value={shown}
        className={`${!text && Math.round(value * 100) === 0 ? 'z' : ''}${invalid ? ' invalid' : ''}${meta?.bucket ? ' bucket' : ''}${meta?.estimate ? ' est' : ''}`}
        title={invalid ? 'Enter a number, or leave the cell blank for zero.' : meta?.estimate ? 'Marked as an estimate / placeholder.' : undefined} aria-invalid={invalid || undefined}
        onKeyDown={(e) => {
          // C19: on a cell with a pay-period chooser, Space opens it — no mouse needed.
          if (e.key === ' ' && allowPeriod && setMeta) {
            e.preventDefault();
            const s = perRef.current;
            if (s) { s.focus(); (s as HTMLSelectElement & { showPicker?: () => void }).showPicker?.(); }
          }
        }}
        onFocus={(e) => { setText(fmt(inPeriod)); requestAnimationFrame(() => e.target.select()); }}
        onChange={(e) => {
          const el = e.target; const raw = el.value; const caret = el.selectionStart ?? raw.length;
          commit(raw.replace(/[$,\s]/g, ''));
          if (decimals) { setText(raw); return; }
          const digitsBefore = raw.slice(0, caret).replace(/[^\d]/g, '').length;
          const grouped = groupInt(raw); setText(grouped);
          requestAnimationFrame(() => {
            let pos = grouped.length, seen = 0;
            if (digitsBefore === 0) pos = /^-/.test(grouped) ? 1 : 0;
            else for (let i = 0; i < grouped.length; i++) if (/\d/.test(grouped[i])) { if (++seen === digitsBefore) { pos = i + 1; break; } }
            try { el.setSelectionRange(pos, pos); } catch { /* detached */ }
          });
        }} />
      {period !== 'monthly' && !focused && <sup className="cell-period" title={`Entered ${INPUT_PERIOD_LABEL[period].toLowerCase()}`}>{PERIOD_BADGE[period]}</sup>}
      {hasNote && !focused && <sup className="cell-note-mark" title={meta?.note}>※</sup>}
      {focused && setMeta && (
        <span className="cellctl" contentEditable={false}>
          {allowPeriod && (
            <select ref={perRef} aria-label="Pay period" className="cellper" value={period} onChange={(e) => setPeriod(e.target.value as InputPeriod)}>
              {INPUT_PERIODS.map((p) => <option key={p} value={p}>{INPUT_PERIOD_LABEL[p]}</option>)}
            </select>
          )}
          <button type="button" className={`cellnote${hasNote ? ' on' : ''}`} title="Footnote for this value" onClick={() => setNoteOpen((o) => !o)}>※ Note</button>
          <button type="button" className={`cellnote${meta?.estimate ? ' on' : ''}`} title="Mark this value as an estimate / placeholder (shown italic; flagged on reports)" onClick={toggleEstimate}>~ Estimate</button>
          {noteOpen && (
            <span className="noteedit">
              <input aria-label="Footnote text" placeholder="e.g. per pay statement dated…" value={meta?.note ?? ''} onChange={(e) => setNote(e.target.value, meta?.noteOnReport ?? true)} />
              <label><input type="checkbox" checked={meta?.noteOnReport ?? false} onChange={(e) => setNote(meta?.note ?? '', e.target.checked)} /> Show on reports</label>
            </span>
          )}
        </span>
      )}
    </span>
  );
}

function TextCell({ value, onChange, label, cellId, type = 'text' }: { value: string; onChange: (s: string) => void; label: string; cellId: string; type?: 'text' | 'date' }) {
  return <input data-cell={cellId} className="txt" type={type} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />;
}

const EX_MAP = { personal: 'personal', dependent: 'dependent', qualifyingChildrenCtc: 'ctc', qualifyingChildrenEic: 'eic', qualifyingChildrenCcc: 'ccc', childrenUnder6: 'under6', otherDependents: 'otherDependents' } as const;

/** A column-resize grip at a header boundary; `onDrag` gets the incremental pixel delta as the pointer moves. */
function ColGrip({ onDrag }: { onDrag: (dx: number) => void }) {
  const last = useRef(0);
  const begin = (e: ReactPointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    last.current = e.clientX;
    const move = (ev: PointerEvent) => { onDrag(ev.clientX - last.current); last.current = ev.clientX; };
    const end = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', end); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', end);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };
  return <span className="colgrip" title="Drag to resize column" aria-hidden="true" onPointerDown={begin} />;
}

export function CaseData() {
  const v = useCaseView();
  const { c, labels, nc } = v;
  const update = useDis((s) => s.update);
  const setUi = useDis((s) => s.setUi);
  const setPrefs = useDis((s) => s.setPrefs);
  const sel = useDis((s) => s.ui.sel);
  const tooltips = useDis((s) => s.ui.tooltips);
  const hideZero = useDis((s) => s.ui.hideZeroGrid);
  const crossHighlight = useDis((s) => s.ui.crossHighlight);
  const gridLabelW = useDis((s) => s.ui.gridLabelW);
  const gridFatherW = useDis((s) => s.ui.gridFatherW);
  const motherW = Math.max(0, 100 - gridLabelW - gridFatherW);
  const [focusCol, setFocusCol] = useState<PartyIndex | null>(null);
  const expanded = v.doc.prefs.expanded;
  const f = factor(c);
  const tableRef = useRef<HTMLTableElement>(null);
  const cellMeta = v.doc.prefs.cellMeta;
  const monthlyMode = c.mode !== 'ANNUAL';
  const setMetaFor = (id: string) => (m: CellMeta | null) => setPrefs((pp) => { if (m) pp.cellMeta[id] = m; else delete pp.cellMeta[id]; });
  const metaProps = (id: string, allowPeriod = false) => ({ meta: cellMeta[id], setMeta: setMetaFor(id), allowPeriod: allowPeriod && monthlyMode });
  // C20: once a non-default sub is categorized, drop the parent's quick-entry "bucket" flag.
  const clearBucket = (rowId: string, p: PartyIndex) => {
    const bid = `${p}:${rowId}`;
    if (cellMeta[bid]?.bucket) setPrefs((pp) => { const m = pp.cellMeta[bid]; if (m) { delete m.bucket; if (!m.period && !m.note) delete pp.cellMeta[bid]; } });
  };

  const isZero = (row: GridRow, sub: SubRow | null) => [0, 1].every((p) => Math.round(moneyValue(c, p as PartyIndex, row, sub) * f) === 0);
  const lines = flattenRows(ROWS, expanded, { hideZero, isZero });
  const visible = lines.filter((l) => l.visible);
  const anyOpen = ROWS.some((r) => r.subs && expanded[r.id]);

  const exTotal = (p: PartyIndex): number => {
    const e = c.parties[p].exemptions;
    if (e.auto && nc) { const x = nc.parties[p].exemptions; return x.personal + x.special + x.newSpouse + x.dependent; }
    return e.personal + e.special + e.newSpouse + e.dependent;
  };

  const cell = (line: FlatLine, p: PartyIndex) => {
    const { row, sub } = line;
    const id = `${p}:${sub?.id ?? row.id}`;
    const lbl = `${sub?.label ?? row.label}, ${labels[p]}`;
    if (sub) {
      const locked = sub.lockedWhen?.(c, p) ?? false;
      if (sub.kind === 'text' || sub.kind === 'date') return <TextCell cellId={id} label={lbl} type={sub.kind === 'date' ? 'date' : 'text'} value={String(sub.get(c, p))} onChange={(s) => update((x) => sub.set(x, p, s))} />;
      if (sub.kind === 'bool') return (
        <select data-cell={id} aria-label={lbl} value={sub.get(c, p) ? 'y' : 'n'} onChange={(e) => update((x) => sub.set(x, p, e.target.value === 'y'))}>
          <option value="y">Yes</option><option value="n">No</option>
        </select>
      );
      if (locked) {
        let shown = Number(sub.get(c, p));
        if (row.id === 'exempt' && nc) { const k = sub.id.replace('ex_', '') as keyof typeof EX_MAP; shown = nc.parties[p].exemptions[EX_MAP[k]]; }
        if (row.id === 'hardship' && v.result) shown = v.result.parties[p].hardshipTotal - c.parties[p].hardship.extraordinaryHealth - c.parties[p].hardship.catastrophicLosses;
        return <span className="ro" title="Automatic value">{sub.kind === 'money' ? fmtInt(shown * f) : fmtInt(shown)}</span>;
      }
      if (sub.kind === 'money') return <NumCell cellId={id} label={lbl} value={Number(sub.get(c, p)) * f} onChange={(n) => { update((x) => sub.set(x, p, n / f)); if (row.defaultSub && sub.id !== row.defaultSub) clearBucket(row.id, p); }} {...metaProps(id, true)} />;
      return <NumCell cellId={id} label={lbl} value={Number(sub.get(c, p))} decimals={sub.kind === 'percent'} onChange={(n) => update((x) => sub.set(x, p, n))} {...metaProps(id)} />;
    }
    switch (row.kind) {
      case 'info': return <span className="ro">{c.info.partyNames[p] || '—'}</span>;
      case 'nkids': { const n = childrenWith(c, p); return <span className={`ro go${n ? '' : ' z'}`} onClick={() => setUi({ left: 'children' })}>{n}</span>; }
      case 'ncp': { const t = timeshareWith(c.children, p); return <span className={`ro go${t ? '' : ' z'}`} onClick={() => setUi({ left: 'children' })}>{Math.round(t * 10) / 10}%</span>; }
      case 'filing': return (
        <select data-cell={id} aria-label={lbl} value={c.parties[p].filingStatus} onChange={(e) => update((x) => { x.parties[p].filingStatus = e.target.value as FilingStatus; })}>
          {FILINGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      );
      case 'exempt': return <NumCell cellId={id} label={lbl} value={exTotal(p)} onChange={(n) => update((x: Case) => {
        const e = x.parties[p].exemptions;
        if (e.auto && nc) { const r = nc.parties[p].exemptions; Object.assign(e, { personal: r.personal, special: r.special, newSpouse: r.newSpouse, qualifyingChildrenCtc: r.ctc, qualifyingChildrenEic: r.eic, qualifyingChildrenCcc: r.ccc, childrenUnder6: r.under6, otherDependents: r.otherDependents }); }
        e.auto = false;
        e.dependent = Math.max(0, Math.round(n) - e.personal - e.special - e.newSpouse);
      })} {...metaProps(id)} />;
      case 'money': return row.get && row.set ? <NumCell cellId={id} label={lbl} value={row.get(c.parties[p]) * f} onChange={(n) => update((x) => row.set?.(x.parties[p], n / f))} {...metaProps(id, true)} /> : null;
      case 'composite': {
        const t = row.total?.(c, p) ?? 0;
        const dsub = row.defaultSub ? row.subs?.find((s) => s.id === row.defaultSub) : undefined;
        const auto = row.auto?.(c, p) ?? false;
        // C20: editable quick-entry only when a default bucket exists, nothing is auto-computed, and the other
        // sub-buckets are empty (so a lump can't clobber an already-categorized breakdown).
        const otherFilled = !!row.subs?.some((s) => s.kind === 'money' && s.id !== row.defaultSub && Math.round(Number(s.get(c, p))) !== 0);
        if (!dsub || auto || otherFilled) return <span className={`ro${Math.round(t * f) ? '' : ' z'}`}>{fmtInt(t * f)}</span>;
        return <NumCell cellId={id} label={`${row.label}, ${labels[p]}`} value={t * f}
          onChange={(n) => {
            update((x) => dsub.set(x, p, n / f));
            setPrefs((pp) => {
              const prev = pp.cellMeta[id] ?? {};
              if (Math.round(n) !== 0) pp.cellMeta[id] = { ...prev, bucket: true };
              else { const { bucket: _b, ...rest } = prev; if (rest.period || rest.note) pp.cellMeta[id] = rest; else delete pp.cellMeta[id]; }
            });
          }}
          {...metaProps(id, true)} />;
      }
      default: return null;
    }
  };

  /** ↑/↓/Enter move between visible rows, keeping the party column when typing. */
  const onKeyDown = (e: KeyboardEvent<HTMLTableElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    const t = e.target as HTMLElement;
    if (t.tagName === 'SELECT' && e.key !== 'Enter') return;
    const idx = visible.findIndex((l) => (l.sub?.id ?? l.row.id) === sel);
    const next = visible[Math.max(0, Math.min(visible.length - 1, idx + (e.key === 'ArrowUp' ? -1 : 1)))];
    if (!next) return;
    e.preventDefault();
    const nid = next.sub?.id ?? next.row.id;
    setUi({ sel: nid, helpTopic: null });
    const col = (t.getAttribute('data-cell') ?? '0:').split(':')[0];
    requestAnimationFrame(() => {
      const el = tableRef.current?.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-cell="${col}:${nid}"]`);
      el?.focus();
    });
  };

  // Keep the selected row in view when it changes from the keyboard.
  useEffect(() => { tableRef.current?.querySelector('tr.sel')?.scrollIntoView({ block: 'nearest' }); }, [sel]);

  // Column resizing. Handlers read live widths from the store each move (not the render closure) so the
  // incremental deltas accumulate. Boundary 1 trades width between Input and the first party; boundary 2
  // between the two party columns. Minimum 12% per column.
  const MINW = 12;
  const dragCol1 = (dx: number) => {
    const t = tableRef.current; if (!t) return;
    const d = (dx / t.clientWidth) * 100; const st = useDis.getState().ui;
    const label = st.gridLabelW + d, father = st.gridFatherW - d;
    if (label < 20 || father < MINW) return;
    setUi({ gridLabelW: label, gridFatherW: father });
  };
  const dragCol2 = (dx: number) => {
    const t = tableRef.current; if (!t) return;
    const d = (dx / t.clientWidth) * 100; const st = useDis.getState().ui;
    const father = st.gridFatherW + d, mother = 100 - st.gridLabelW - father;
    if (father < MINW || mother < MINW) return;
    setUi({ gridFatherW: father });
  };

  return (
    <>
      <div className="gridbar">
        <span>{periodWord(c)} amounts · tax year {c.taxYear}</span>
        <span className="sp" />
        <button type="button" className="lnkbtn" title="Highlight the parent column you're editing (Excel-style focus cell)" onClick={() => setUi({ crossHighlight: !crossHighlight })}>{crossHighlight ? '☑' : '☐'} Focus highlight</button>
        <button type="button" className="lnkbtn" title="Z" onClick={() => act('toggleZero')}>{hideZero ? 'Show zero rows' : 'Hide zero rows'}</button>
        <button type="button" className="lnkbtn" title="X" onClick={() => act(anyOpen ? 'collapseAll' : 'expandAll')}>{anyOpen ? 'Collapse all' : 'Expand all'}</button>
      </div>
      <table className={`grid${crossHighlight ? ' xhl' : ''}${focusCol === 0 ? ' cx0' : focusCol === 1 ? ' cx1' : ''}`} id="grid" ref={tableRef} onKeyDown={onKeyDown}
        onFocusCapture={(e) => { const dc = (e.target as HTMLElement).getAttribute?.('data-cell'); const p = dc?.split(':')[0]; if (p === '0' || p === '1') setFocusCol(Number(p) as PartyIndex); }}
        onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocusCol(null); }}>
        <colgroup><col className="c-l" style={{ width: `${gridLabelW}%` }} /><col style={{ width: `${gridFatherW}%` }} /><col style={{ width: `${motherW}%` }} /></colgroup>
        <thead><tr><th scope="col">Input<ColGrip onDrag={dragCol1} /></th><th scope="col" className="pa">{labels[0]}<ColGrip onDrag={dragCol2} /></th><th scope="col" className="pb">{labels[1]}</th></tr></thead>
        <tbody>
          {lines.map((line) => {
            if (!line.visible) return null;
            const { row, sub } = line;
            const id = sub?.id ?? row.id;
            const cls = [sub ? 'sub' : '', line.shade % 2 ? 'alt' : '', sel === id ? 'sel' : ''].filter(Boolean).join(' ');
            const help = tooltips ? helpFor(id)?.[0] : undefined;
            const auto = (p: PartyIndex) => (!sub && row.auto?.(c, p) ? ' auto' : '');
            const isOpen = !!expanded[row.id];
            return (
              <tr key={id} data-row={id} className={cls} onMouseDown={() => { if (sel !== id) setUi({ sel: id, helpTopic: null }); }} onFocus={() => { if (sel !== id) setUi({ sel: id, helpTopic: null }); }}>
                <td className="l" title={help}>
                  {!sub && (row.subs
                    ? <button type="button" className="tri" aria-expanded={isOpen} aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${row.label}`} onClick={() => setPrefs((pr) => { pr.expanded[row.id] = !isOpen; })}>{isOpen ? '▼' : '▶'}</button>
                    : <span className="tri ph" />)}
                  {sub?.label ?? row.label}
                </td>
                <td className={`v p0${auto(0)}`}>{cell(line, 0)}</td>
                <td className={`v p1${auto(1)}`}>{cell(line, 1)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ padding: '8px 12px', color: 'var(--muted)', fontSize: 11.5 }}>
        * automatic value. ▶ rows expand to their detail lines. Choose a row to see its help on the Help tab.{hideZero ? ' Zero rows are hidden (Z shows them).' : ''}
      </div>
    </>
  );
}
